import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  FileText,
  Calendar,
  Clock,
  TicketCheck,
  Briefcase,
  Users,
  UserPlus,
  MessageCircle,
  CheckCircle2,
  Download,
  Package,
  Wallet,
  GraduationCap,
  Plane,
  Landmark,
  BadgeCheck,
  Building,
  Shield,
  AlertTriangle,
  Mail,
  User,
  ChevronRight,
  Inbox,
  ArrowRight,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { employerGreetingText } from '../../lib/dashboardGreeting';
import { moduleAccessLevel } from '../../lib/permissions';
import { resolveCandidateStage, candidateStageLabel, STAGE_ORDER } from '../../lib/referrerStages';
import { StatCardRow, type StatCardData } from './StatCard';
import { SkeletonCardGrid, Skeleton, SkeletonRow } from './Skeleton';
import EmptyState from './EmptyState';

/**
 * Permission helper used for the workspace team-member (role === 'admin')
 * dashboard. Every widget is derived from a module that the member may or may
 * not hold, so the shell consults `moduleAccessLevel` and only renders the
 * modules actually granted. No Access → the card/widget is removed entirely.
 */
function memberAccess(module: string, profile: any): boolean {
  if (!profile) return true;
  const isPlatformAdmin = !!(profile?.platformAdmin || profile?.isPlatformAdmin);
  return moduleAccessLevel(module, profile, isPlatformAdmin) !== 'none';
}

export type DashboardRole = 'admin' | 'applicant' | 'employer' | 'referrer' | 'sponsor';

interface DashboardShellProps {
  role: DashboardRole;
  userProfile: any;
  /** Navigate to another section of the portal (sidebar tab id). */
  onNavigate?: (tabId: string) => void;
  /** Optional pre-loaded data to avoid duplicate fetches (from parent dashboards). */
  applications?: any[];
  events?: any[];
  tickets?: any[];
  activityLogs?: any[];
  stats?: any;
  candidates?: any[];
  jobs?: any[];
  loading?: boolean;
}

const ACTIVE_EVENT_STATUSES = ['Scheduled', 'Pending', 'Awaiting Response', 'Confirmed', 'Rescheduled'];

interface RoleData {
  apps: any[];
  events: any[];
  tickets: any[];
  activity: ActivityItem[];
  activityLogs?: any[];
  stats: any;
}

/* ── Per-role greeting + contextual subhead ── */
const GREETING: Record<DashboardRole, (profile: any, data: RoleData) => string> = {
  admin: () => "Here's what's happening across your organization today.",
  applicant: (_p, d) =>
    d.apps.length > 0
      ? `You have ${d.apps.length} application${d.apps.length === 1 ? '' : 's'} on file. Keep an eye on your next steps below.`
      : "Here's what's happening with your application.",
  employer: (_p, d) => employerGreetingText(d.stats?.activeJobs),
  referrer: (_p, d) =>
    d.stats?.totalReferrals
      ? `You've submitted ${d.stats.totalReferrals} referral${d.stats.totalReferrals === 1 ? '' : 's'} so far.`
      : "Here's what's happening with your referrals.",
  sponsor: (_p, d) =>
    d.apps.length > 0
      ? `${d.apps.length} applicant${d.apps.length === 1 ? '' : 's'} assigned to your sponsorship program.`
      : "Here's what's happening with your sponsored applicants.",
};

export interface ActivityItem {
  id: string;
  title: string;
  description?: string;
  timestamp?: string;
  icon: LucideIcon;
  tone?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
}

/* ── Stat card definitions per role (computed from existing data) ── */
function buildStatCards(role: DashboardRole, data: RoleData, onNavigate?: (t: string) => void, userProfile?: any): StatCardData[] {
  const { apps, events, tickets, stats } = data;
  const openTickets = tickets.filter((t) => !['resolved', 'closed', 'archived'].includes(t.status || '')).length;

  if (role === 'admin') {
    const cards: StatCardData[] = [];
    const pendingApprovals = stats?.pendingApprovals ?? 0;
    const totalApplicants = apps.length;

    // No Access to a module ⇒ its dashboard card must not render at all.
    if (memberAccess('applicants', userProfile)) {
      cards.push({
        id: 'total-applicants',
        label: 'Total Applicants',
        value: totalApplicants,
        icon: Users,
        isHero: true,
        trend: totalApplicants > 0 ? { text: '+org-wide', direction: 'up' } : undefined,
        onClick: onNavigate ? () => onNavigate('applicants') : undefined,
      });
    }
    if (memberAccess('tickets', userProfile)) {
      cards.push({
        id: 'open-tickets',
        label: 'Open Support Tickets',
        value: openTickets,
        icon: TicketCheck,
        trend: openTickets > 0 ? { text: `${openTickets} need attention`, direction: 'down' } : { text: 'All clear', direction: 'flat' },
        onClick: onNavigate ? () => onNavigate('tickets') : undefined,
      });
      cards.push({
        id: 'sla-breaches',
        label: 'SLA Breaches This Week',
        value: 0,
        icon: AlertTriangle,
        trend: { text: '0 breaches', direction: 'flat' },
      });
    }
    if (memberAccess('approvals', userProfile)) {
      cards.push({
        id: 'pending-approvals',
        label: 'Pending Role Approvals',
        value: pendingApprovals,
        icon: Shield,
        trend: pendingApprovals > 0 ? { text: `${pendingApprovals} waiting`, direction: 'down' } : { text: 'Up to date', direction: 'flat' },
        onClick: onNavigate ? () => onNavigate('approvals') : undefined,
      });
    }
    return cards;
  }

  if (role === 'applicant') {
    const daysSinceUpdate = apps.length
      ? Math.max(0, Math.floor((Date.now() - new Date(apps.map((a) => a.updatedAt || a.appliedAt || Date.now()).sort().pop()).getTime()) / 86400000))
      : 0;
    const submitted = apps.filter((a) => (a.status || '').toLowerCase() === 'submitted').length;
    const inReview = apps.filter((a) => /review|interview|shortlist/i.test(a.status || '')).length;
    return [
      {
        id: 'applications',
        label: 'Applications',
        value: apps.length,
        icon: FileText,
        isHero: true,
        trend: apps.length > 0 ? { text: `${submitted} submitted`, direction: 'flat' } : undefined,
        onClick: onNavigate ? () => onNavigate('application') : undefined,
      },
      {
        id: 'in-review',
        label: 'In Review',
        value: inReview,
        icon: MessageCircle,
        trend: inReview > 0 ? { text: 'Being reviewed', direction: 'flat' } : undefined,
      },
      {
        id: 'days-since-update',
        label: 'Days Since Last Update',
        value: daysSinceUpdate,
        icon: Clock,
        trend: daysSinceUpdate > 7 ? { text: 'Needs attention', direction: 'down' } : { text: 'Up to date', direction: 'up' },
      },
      {
        id: 'open-tickets',
        label: 'Open Support Tickets',
        value: openTickets,
        icon: TicketCheck,
        trend: openTickets > 0 ? { text: `${openTickets} open`, direction: 'down' } : { text: 'All clear', direction: 'flat' },
        onClick: onNavigate ? () => onNavigate('support') : undefined,
      },
    ];
  }

  if (role === 'employer') {
    const interviewsThisWeek = events.filter((e) => {
      const d = new Date(e.dateTime || 0).getTime();
      const weekFromNow = Date.now() + 7 * 86400000;
      return d >= Date.now() && d <= weekFromNow;
    }).length;
    const fillPct = stats?.totalApplicants ? Math.round(((stats.hiredCandidates || 0) / stats.totalApplicants) * 100) : 0;
    return [
      {
        id: 'active-jobs',
        label: 'Active Job Postings',
        value: stats?.activeJobs ?? 0,
        icon: Briefcase,
        isHero: true,
        trend: (stats?.newApplicants ?? 0) > 0 ? { text: `${stats.newApplicants} new applicants`, direction: 'up' } : undefined,
        onClick: onNavigate ? () => onNavigate('jobs') : undefined,
      },
      {
        id: 'pipeline',
        label: 'Candidates in Pipeline',
        value: stats?.totalApplicants ?? 0,
        icon: Users,
        onClick: onNavigate ? () => onNavigate('applicants') : undefined,
      },
      {
        id: 'interviews',
        label: 'Interviews This Week',
        value: interviewsThisWeek,
        icon: Calendar,
        onClick: onNavigate ? () => onNavigate('my-events') : undefined,
      },
      {
        id: 'fill-rate',
        label: 'Open Positions Filled',
        value: fillPct,
        suffix: '%',
        icon: CheckCircle2,
        trend: fillPct > 0 ? { text: `${stats?.hiredCandidates || 0} hired`, direction: 'up' } : { text: 'No hires yet', direction: 'flat' },
      },
    ];
  }

  if (role === 'referrer') {
    // §2.2: pipeline counts derive from the canonical workflow stage, never
    // from legacy free-text stage/status strings.
    const stageIdx = (a: any) => STAGE_ORDER.indexOf(resolveCandidateStage(a));
    const converted = apps.filter((a) => stageIdx(a) >= 1).length;
    const inProgress = apps.filter((a) => { const i = stageIdx(a); return i >= 1 && i < STAGE_ORDER.length - 1; }).length;
    const pendingPayouts = stats?.pendingCommissions ?? 0;
    // "Payable" is a lifecycle claim, not a synonym for pending: a
    // commission only becomes payable once an admin has committed it,
    // the applicant's balance cleared, and it has been QUEUED for payout.
    const queuedPayouts = stats?.queuedCommissions ?? 0;
    return [
      {
        id: 'referrals',
        label: 'Total Referrals',
        value: stats?.totalReferrals ?? apps.length,
        icon: UserPlus,
        isHero: true,
        onClick: onNavigate ? () => onNavigate('candidates') : undefined,
      },
      {
        id: 'converted',
        label: 'Converted to Applicants',
        value: converted,
        icon: Users,
        trend: converted > 0 ? { text: 'Active pipeline', direction: 'up' } : undefined,
      },
      {
        id: 'in-progress',
        label: 'Referrals In Progress',
        value: inProgress,
        icon: MessageCircle,
      },
      {
        id: 'payouts',
        label: 'Pending Payouts',
        value: pendingPayouts,
        prefix: '$',
        icon: Wallet,
        trend: queuedPayouts > 0 ? { text: 'Payable', direction: 'up' } : pendingPayouts > 0 ? { text: 'Pending', direction: 'flat' } : { text: 'No payouts', direction: 'flat' },
      },
    ];
  }

  // sponsor
  const pendingSevis = apps.filter((a) => {
    const r = (a as any).readiness;
    return r === 'not_ready' || (a as any).sevisId ? false : !(a as any).sevisId;
  }).length;
  const exportedThisMonth = apps.filter((a) => {
    const d = (a as any).sevisExportedAt || (a as any).ds2019IssuedAt;
    if (!d) return false;
    const date = new Date(d);
    return date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear();
  }).length;
  const feeOutstanding = apps.filter((a) => (a as any).programFeeStatus === 'unpaid' || !(a as any).programFeeStatus).length;
  return [
    {
      id: 'pending-sevis',
      label: 'Pending SEVIS Export',
      value: pendingSevis,
      icon: Download,
      isHero: true,
      trend: pendingSevis > 0 ? { text: 'Action needed', direction: 'down' } : { text: 'All exported', direction: 'flat' },
      onClick: onNavigate ? () => onNavigate('applicants') : undefined,
    },
    {
      id: 'ds2019',
      label: 'DS-2019s Issued This Month',
      value: exportedThisMonth,
      icon: GraduationCap,
      trend: exportedThisMonth > 0 ? { text: 'On track', direction: 'up' } : { text: 'None yet', direction: 'flat' },
    },
    {
      id: 'fee-outstanding',
      label: 'Fee Confirmations Outstanding',
      value: feeOutstanding,
      icon: Wallet,
      trend: feeOutstanding > 0 ? { text: `${feeOutstanding} pending`, direction: 'down' } : { text: 'All confirmed', direction: 'flat' },
      onClick: onNavigate ? () => onNavigate('applicants') : undefined,
    },
  ];
}

/* ── Activity feed per role (from existing data) ── */
function buildActivity(role: DashboardRole, data: RoleData, tickets: any[], userProfile?: any): ActivityItem[] {
  const { apps, events, activityLogs } = data;
  const items: ActivityItem[] = [];

  if (role === 'sponsor') {
    (activityLogs || []).slice(0, 8).forEach((log: any) => {
      items.push({
        id: log.logId || `log_${Math.random()}`,
        title: log.action === 'sponsor_export' ? 'SEVIS export completed' : log.action === 'sevis_record_entry' ? 'SEVIS record entered' : (log.action || 'Update'),
        description: log.details || log.message || `Applicant ${log.applicantName || 'record'} updated`,
        timestamp: log.timestamp,
        icon: log.action === 'sponsor_export' ? Download : log.action === 'sevis_record_entry' ? CheckCircle2 : Sparkles,
        tone: log.action === 'sponsor_export' ? 'success' : 'info',
      });
    });
  } else if (role === 'applicant') {
    apps.slice(0, 8).forEach((a) => {
      items.push({
        id: `app_${a.id || a.applicationId}`,
        title: (a.job?.title || 'Application') + ' — ' + (a.status || 'Updated'),
        description: `Last activity ${a.updatedAt ? new Date(a.updatedAt).toLocaleDateString() : 'recently'}`,
        timestamp: a.updatedAt,
        icon: FileText,
        tone: /approved|hired/.test(a.status || '') ? 'success' : /reject|return/i.test(a.status || '') ? 'danger' : 'info',
      });
    });
  } else if (role === 'employer') {
    events.slice(0, 4).forEach((e) => {
      items.push({
        id: `evt_${e.eventId}`,
        title: `Interview: ${e.title || 'Meeting'}`,
        description: `${new Date(e.dateTime).toLocaleString()} — ${e.status || 'Scheduled'}`,
        timestamp: e.dateTime,
        icon: Calendar,
        tone: 'info',
      });
    });
    if (data.stats?.newApplicants) {
      items.unshift({
        id: 'new-apps',
        title: `${data.stats.newApplicants} new candidate application${data.stats.newApplicants === 1 ? '' : 's'} this week`,
        description: 'Awaiting your review',
        icon: UserPlus,
        tone: 'success',
      });
    }
  } else if (role === 'referrer') {
    apps.slice(0, 8).forEach((c) => {
      const stageKey = resolveCandidateStage(c);
      items.push({
        id: `ref_${c.id || c.applicationId}`,
        title: `${c.firstName || 'Candidate'} ${c.lastName || ''} — ${candidateStageLabel(c)}`,
        description: c.daysInStage ? `In stage ${c.daysInStage} days` : 'Referral status updated',
        timestamp: c.updatedAt || c.createdAt,
        icon: UserPlus,
        tone: stageKey === 'closed' ? 'success' : 'info',
      });
    });
  } else {
    // admin — recent tickets + newest applications. Each feed section is only
    // rendered when the member actually holds the backing module permission.
    if (memberAccess('tickets', userProfile)) {
      tickets.slice(0, 4).forEach((t) => {
        items.push({
          id: `tkt_${t.ticketId}`,
          title: `Support ticket: ${t.subject || 'Untitled'}`,
          description: `Status: ${t.status || 'open'}`,
          timestamp: t.updatedAt || t.createdAt,
          icon: TicketCheck,
          tone: t.status === 'resolved' ? 'success' : 'warning',
        });
      });
    }
    if (memberAccess('applicants', userProfile)) {
      apps.slice(0, 4).forEach((a) => {
        items.push({
          id: `admin_app_${a.id || a.applicationId}`,
          title: `Application ${a.status || 'updated'}`,
          description: (a.userEmail || a.email || 'Applicant') + (a.programName ? ` • ${a.programName}` : ''),
          timestamp: a.updatedAt || a.appliedAt,
          icon: FileText,
          tone: 'info',
        });
      });
    }
  }

  return items.slice(0, 8);
}

/* ── Calendar snapshot widget ── */
function CalendarSnapshot({ events, onNavigate, role }: { events: any[]; onNavigate?: (t: string) => void; role: DashboardRole }) {
  const upcoming = useMemo(() => {
    const now = Date.now();
    const seenKeys = new Set<string>();
    const deduplicated: any[] = [];
    for (const e of events) {
      if (!e || !e.dateTime) continue;
      const key = e.eventId || `${e.title || ''}_${e.dateTime}`;
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      deduplicated.push(e);
    }
    return deduplicated
      .filter((e) => e.dateTime && new Date(e.dateTime).getTime() >= now && ACTIVE_EVENT_STATUSES.includes(e.status || ''))
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
      .slice(0, 5);
  }, [events]);

  return (
    <section className="dashboard-widget">
      <div className="dashboard-widget-header">
        <h3 className="dashboard-widget-title">
          <Calendar className="h-4 w-4" /> Upcoming
        </h3>
        {onNavigate && (
          <button onClick={() => onNavigate('my-events')} className="text-xs font-semibold text-brand hover:underline cursor-pointer inline-flex items-center gap-1">
            View calendar <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {upcoming.length === 0 ? (
        <EmptyState
          icon={Calendar}
          compact
          title={role === 'applicant' ? 'No upcoming dates' : 'Nothing scheduled'}
          message="New events and deadlines will appear here."
        />
      ) : (
        <ul className="space-y-2">
          {upcoming.map((e) => {
            const d = new Date(e.dateTime);
            return (
              <li key={e.eventId} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5">
                <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-white border border-slate-200 shadow-sm">
                  <span className="text-[10px] font-bold uppercase text-slate-400">{d.toLocaleDateString(undefined, { month: 'short' })}</span>
                  <span className="text-sm font-extrabold leading-none text-slate-800">{d.getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{e.title || 'Meeting'}</p>
                  <p className="truncate text-[11px] text-slate-500">{e.eventType || 'Event'} • {d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${e.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-700' : e.status === 'Cancelled' ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-700'}`}>
                  {e.status || 'Scheduled'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ── Activity feed widget ── */
function ActivityFeed({ items }: { items: ActivityItem[] }) {
  return (
    <section className="dashboard-widget">
      <div className="dashboard-widget-header">
        <h3 className="dashboard-widget-title">
          <Clock className="h-4 w-4" /> Recent Activity
        </h3>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Inbox} compact title="No recent activity" message="Updates about your applications and tickets will show up here." />
      ) : (
        <ul className="space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            const toneClass =
              item.tone === 'success' ? 'bg-emerald-50 text-emerald-600'
              : item.tone === 'danger' ? 'bg-rose-50 text-rose-600'
              : item.tone === 'warning' ? 'bg-amber-50 text-amber-600'
              : 'bg-blue-50 text-blue-600';
            return (
              <li key={item.id} className="flex items-start gap-3 rounded-xl px-2 py-2.5 hover:bg-slate-50 transition-colors">
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${toneClass}`}>
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{item.title}</p>
                  {item.description && <p className="truncate text-[11px] text-slate-500">{item.description}</p>}
                </div>
                {item.timestamp && (
                  <time className="shrink-0 text-[10px] font-medium text-slate-400">{formatRelative(new Date(item.timestamp).getTime())}</time>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ── Support tickets widget ── */
function SupportTicketsWidget({ tickets, onNavigate }: { tickets: any[]; onNavigate?: (t: string) => void }) {
  const open = tickets.filter((t) => !['resolved', 'closed', 'archived'].includes(t.status || '')).length;
  const closed = tickets.filter((t) => ['resolved', 'closed'].includes(t.status || '')).length;
  const latest = tickets[0];

  return (
    <section className="dashboard-widget">
      <div className="dashboard-widget-header">
        <h3 className="dashboard-widget-title">
          <TicketCheck className="h-4 w-4" /> Support Tickets
        </h3>
        {onNavigate && (
          <button onClick={() => onNavigate('tickets')} className="text-xs font-semibold text-brand hover:underline cursor-pointer inline-flex items-center gap-1">
            Open inbox <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {tickets.length === 0 ? (
        <EmptyState icon={TicketCheck} compact title="No support tickets" message="You're all caught up — no open tickets right now." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
              <p className="text-2xl font-extrabold text-slate-900">{open}</p>
              <p className="text-[11px] font-medium text-slate-500">Open</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
              <p className="text-2xl font-extrabold text-slate-900">{closed}</p>
              <p className="text-[11px] font-medium text-slate-500">Closed</p>
            </div>
          </div>
          {latest && (
            <div className="rounded-xl border border-slate-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold text-slate-800">{latest.subject || 'Ticket'}</p>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${open ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                  {latest.status || 'open'}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-slate-500">Latest ticket • {formatRelative(new Date(latest.updatedAt || latest.createdAt).getTime())}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(ts).toLocaleDateString();
}

/**
 * DashboardShell — the universal dashboard home page (Section 4).
 *
 * One shell for every role: greeting header, role-specific stat cards,
 * calendar snapshot, activity feed, and support tickets widget. All data is
 * pulled from existing API endpoints (no new backend) with loading skeletons
 * and designed empty states.
 */
export default function DashboardShell({
  role,
  userProfile,
  onNavigate,
  applications: appsProp,
  events: eventsProp,
  tickets: ticketsProp,
  activityLogs: activityLogsProp,
  stats: statsProp,
  candidates: candidatesProp,
  loading: loadingProp,
}: DashboardShellProps) {
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});

  // Self-fetch role-appropriate data from existing endpoints when the parent
  // doesn't pass pre-loaded data (keeps this a drop-in universal component).
  useEffect(() => {
    if (loadingProp !== undefined) {
      setLoading(loadingProp);
      if (!loadingProp) setLoading(false);
    }
  }, [loadingProp]);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      if (loadingProp !== undefined) return;
      setLoading(true);
      try {
        const [ticketRes, eventRes] = await Promise.all([
          safeFetch('/api/tickets').catch(() => ({ tickets: [] })),
          safeFetch('/api/events/mine').catch(() => ({ events: [] })),
        ]);
        if (cancelled) return;
        setTickets(ticketRes.tickets || []);
        setEvents(eventRes.events || []);

        if (role === 'applicant') {
          const res = await safeFetch('/api/applicant/applications').catch(() => ({ applications: [], matches: [] }));
          if (cancelled) return;
          const list = [...(res.applications || []), ...(res.matches || [])];
          setApps(list);
        } else if (role === 'employer') {
          const res = await safeFetch('/api/employer/stats').catch(() => ({ stats: null }));
          if (cancelled) return;
          setStats(res.stats || {});
          const jobsRes = await safeFetch('/api/employer/jobs').catch(() => ({ jobs: [] }));
          if (!cancelled) setStats((s: any) => ({ ...s, jobs: jobsRes.jobs || [] }));
        } else if (role === 'referrer') {
          const res = await safeFetch('/api/referrer/candidates').catch(() => ({ candidates: [] }));
          if (cancelled) return;
          setApps(res.candidates || []);
        } else if (role === 'sponsor') {
          const res = await safeFetch('/api/sponsor/applications').catch(() => ({ applications: [] }));
          if (cancelled) return;
          setApps(res.applications || []);
          const auditRes = await safeFetch('/api/sponsor/audit').catch(() => ({ logs: [], activityLogs: [] }));
          if (!cancelled) setActivityLogs(auditRes.activityLogs || auditRes.logs || []);
        } else {
          // admin
          const appsRes = await safeFetch('/api/admin/applications?limit=200').catch(() => ({ applications: [] }));
          if (cancelled) return;
          setApps(appsRes.applications || []);
          const pendRes = await safeFetch('/api/admin/pending-approvals').catch(() => ({ pending: [], count: 0 }));
          if (!cancelled) {
            // Endpoint returns {employers, sponsors, referrers, jobs} arrays — sum them for the real count
            const total = (pendRes.employers || []).length + (pendRes.sponsors || []).length + (pendRes.referrers || []).length + (pendRes.jobs || []).length;
            setStats({ pendingApprovals: pendRes.count ?? total });
          }
        }
      } catch {
        /* endpoints are individually caught above */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [role]);

  const firstName = userProfile?.firstName || userProfile?.displayName || userProfile?.personalDetails?.firstName || 'there';
  const roleData: RoleData = {
    // For referrer, the parent's candidate list doubles as the apps list.
    apps: appsProp !== undefined ? appsProp : (role === 'referrer' && candidatesProp !== undefined ? candidatesProp : apps),
    events: eventsProp !== undefined ? eventsProp : events,
    tickets: ticketsProp !== undefined ? ticketsProp : tickets,
    activity: [],
    stats: statsProp !== undefined ? statsProp : stats,
  };

  const statCards = useMemo(() => buildStatCards(role, roleData, onNavigate, userProfile), [role, roleData.apps, roleData.events, roleData.tickets, roleData.stats, userProfile]);
  const activityItems = useMemo(() => buildActivity(role, { ...roleData, activityLogs: activityLogsProp !== undefined ? activityLogsProp : activityLogs }, roleData.tickets, userProfile), [role, roleData, activityLogs, userProfile]);

  const isLoading = loadingProp !== undefined ? !!loadingProp : loading;

  // Widget-level gating for workspace team members (role === 'admin'). When a
  // module is No Access its widget is removed from the dashboard completely —
  // not merely emptied or hidden with CSS.
  const adminShowCalendar = role !== 'admin' || memberAccess('events', userProfile) || memberAccess('my-events', userProfile);
  const adminShowTickets = role !== 'admin' || memberAccess('tickets', userProfile);

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Greeting header */}
      <header className="dashboard-widget bg-gradient-to-br from-white via-white to-brand-primary-light/30 border-brand-primary-light/40">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-[22px] sm:text-2xl font-bold tracking-tight text-slate-900">
              Hi, {firstName} <span aria-hidden="true">👋</span>
            </h1>
            <p className="mt-1 text-sm text-slate-500">{GREETING[role](userProfile, roleData)}</p>
          </div>
          {onNavigate && (
            <div className="flex gap-2">
              {role === 'applicant' && (() => {
                // §3.1: the CTA must match reality — "Continue Application"
                // only when an editable (Draft/Returned) application
                // actually exists. A submitted-only applicant gets "View
                // Progress"; no applications yet gets "Start an
                // Application". (Job matches merged into this list are not
                // applications and never drive the label.)
                const realApps = (roleData.apps || []).filter((a: any) => a && (a.applicationId || a.submissionStatus || a.status));
                const hasEditable = realApps.some((a: any) => {
                  const s = a.submissionStatus || a.status || 'Draft';
                  return s === 'Draft' || s === 'Returned';
                });
                const label = hasEditable ? 'Continue Application' : realApps.length > 0 ? 'View Progress' : 'Start an Application';
                return (
                  <button onClick={() => onNavigate('application')} className="btn btn-primary text-xs w-full sm:w-auto justify-center min-h-[48px]">
                    <FileText className="h-4 w-4" /> {label}
                  </button>
                );
              })()}
              {role === 'employer' && (
                <button onClick={() => onNavigate('jobs')} className="btn btn-primary text-xs">
                  <Briefcase className="h-4 w-4" /> Post a Job
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Stat cards */}
      {isLoading ? (
        <SkeletonCardGrid count={4} />
      ) : (
        <StatCardRow cards={statCards} />
      )}

      {/* Calendar + Activity + Tickets — widgets for restricted modules are
          not rendered at all (No Access must not show a widget or empty-state
          for a module the member cannot see). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
        {adminShowCalendar && (
          <div className={role === 'admin' ? 'xl:col-span-1' : 'lg:col-span-1 xl:col-span-1'}>
            {isLoading ? (
              <div className="dashboard-widget"><Skeleton className="h-4 w-32" /><SkeletonRow lines={3} /></div>
            ) : (
              <CalendarSnapshot events={eventsProp !== undefined ? eventsProp : events} onNavigate={onNavigate} role={role} />
            )}
          </div>
        )}
        <div className={!adminShowCalendar && !adminShowTickets ? 'lg:col-span-2 xl:col-span-3' : 'lg:col-span-1 xl:col-span-1'}>
          {isLoading ? (
            <div className="dashboard-widget"><Skeleton className="h-4 w-32" /><SkeletonRow lines={3} /></div>
          ) : (
            <ActivityFeed items={activityItems} />
          )}
        </div>
        {adminShowTickets && (
          <div className="lg:col-span-2 xl:col-span-1">
            {isLoading ? (
              <div className="dashboard-widget"><Skeleton className="h-4 w-32" /><SkeletonRow lines={3} /></div>
            ) : (
              <SupportTicketsWidget tickets={roleData.tickets} onNavigate={onNavigate} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
